import {
  EActionCategory,
  EAgentToolType,
  EDatasourceType,
  IActionDefinitionDto,
  IAiAgentElkNodeData,
  ICommonSettings,
  ICrewAgent,
  ICrewManagerAgent
} from "@kuika/kuika-cl-model";
import ELK from "elkjs/lib/elk.bundled";
import { Guid } from "guid-typescript";
import { Edge, Node } from "reactflow";
import { getAmapMapActionSettings } from "../../../../shared/utility/designer-action-parameter-editor-actions";

export const AGENT_TOOLS: {
  group: string;
  name: string;
  displayName?: string;
  description: string;
  icon: string;
  mcpConfigName?:
    | "braveSearchSettings"
    | "slackSettings"
    | "dropboxSettings"
    | "githubSettings"
    | "googleMcpSettings"
    | "googleMapMcpSettings"
    | "jiraMcpSettings"
    | "youtubeMcpSettings"
    | "trelloMcpSettings"
    | "office365McpSettings"
    | "firecrawlMcpSettings"
    | "apifySettings";
}[] = [
  {
    group: "Project & Task Management",
    name: "TrelloMCP",
    displayName: "Trello Boards",
    icon: "trello",
    description: "This MCP server can be used to connect Trello.",
    mcpConfigName: "trelloMcpSettings"
  },
  {
    group: "Project & Task Management",
    name: "JiraMCP",
    displayName: "Jira Tasks",
    icon: "jira",
    description: "This MCP server can be used to connect Jira.",
    mcpConfigName: "jiraMcpSettings"
  },
  {
    group: "File & Storage",
    name: "DropboxMCP",
    displayName: "Dropbox Files",
    icon: "dropbox",
    description: "This MCP server can be used to connect Dropbox",
    mcpConfigName: "dropboxSettings"
  },
  {
    group: "File & Storage",
    name: "GoogleWorkspaceMCP",
    displayName: "Google Workspace",
    icon: "google",
    description:
      "This MCP server can be used to connect Google Calendar, Drive, Gmail, Docs, Sheets, Slides, Forms, Tasks, and Chat services.",
    mcpConfigName: "googleMcpSettings"
  },
  {
    group: "File & Storage",
    name: "FileReadTool",
    displayName: "Open File",
    icon: "mcp",
    description:
      "This tool enables reading the content of local files by providing a full file path. It supports plaintext-based formats such as .txt, .csv, .json, .py, and other readable file types. Upon execution, the tool returns the entire content of the file as a string. This is particularly useful for loading prompt templates, processing logs, reading AI-generated outputs, or integrating external text-based configurations into workflows."
  },
  {
    group: "File & Storage",
    name: "FileWriterTool",
    displayName: "Save File",
    icon: "file-writer-tool",
    description:
      "This tool allows writing structured or unstructured text data into files, supporting various formats such as .txt, .csv, .json, .py, and other plaintext-based files. Given a filename, directory path, content, and an optional overwrite flag, it ensures efficient file creation and content writing. For CSV files, users can structure data in a comma-separated format, making it compatible with spreadsheet applications. This tool is particularly useful for logging, data serialization, and automated script generation in AI-driven workflows."
  },
  {
    group: "File & Storage",
    name: "ExcelReadTool",
    displayName: "Read Excel",
    icon: "excel-read-tool",
    description:
      "This tool enables seamless extraction of structured data from Excel files (.xlsx). Given the full file path of an Excel document, it utilizes pandas with the openpyxl engine to efficiently load the file’s contents. The extracted data is converted into a JSON-like dictionary format, making it easy to integrate with downstream AI models or analytical processes. This tool is particularly useful for processing tabular datasets, performing data enrichment, or transforming spreadsheet-based insights into machine-readable formats for further computation."
  },
  {
    group: "Collaboration & Communication",
    name: "SlackMCP",
    displayName: "Slack Messages",
    icon: "slack",
    description: "This MCP server can be used to connect Slack",
    mcpConfigName: "slackSettings"
  },
  {
    group: "Collaboration & Communication",
    name: "GithubMCP",
    displayName: "Github Repos",
    icon: "github",
    description: "This MCP server can be used to connect Github",
    mcpConfigName: "githubSettings"
  },
  {
    group: "Collaboration & Communication",
    name: "YoutubeMCP",
    displayName: "Youtube Content",
    icon: "youtube",
    description: "This MCP server can be used to connect Youtube.",
    mcpConfigName: "youtubeMcpSettings"
  },
  {
    group: "Collaboration & Communication",
    name: "AirbnbMCP",
    displayName: "Airbnb Data",
    icon: "airbnb",
    description: "This MCP server can be used to connect Airbnb to list suitable places to stay"
  },
  {
    group: "Collaboration & Communication",
    name: "Office365MCP",
    displayName: "Office365",
    icon: "office365",
    description:
      "This MCP server can be used to connect Office365 services like Microsoft Calendar, OneDrive, Outlook, Planner.",
    mcpConfigName: "office365McpSettings"
  },
  {
    group: "Data & Analytics",
    name: "CodeInterpreterTool",
    displayName: "Code Runner",
    icon: "code-interpreter-tool",
    description:
      "This tool enables the Agent to execute Python 3 code that it has generated autonomously. The code is run in a secure, isolated environment, ensuring safety regardless of the content."
  },
  {
    group: "Data & Analytics",
    name: "ScrapeWebsiteTool",
    displayName: "Web Scraper",
    icon: "web-scraper-tool",
    description:
      "A tool designed to extract and read the content of a specified website. It is capable of handling various types of web pages by making HTTP requests and parsing the received HTML content. This tool can be particularly useful for web scraping tasks, data collection, or extracting specific information from websites"
  },
  {
    group: "Data & Analytics",
    name: "FirecrawlMCP",
    displayName: "Dynamic Website Scraper",
    icon: "web-scraper-tool",
    description:
      "A tool designed to extract and read the content of a specified dynamic website. It is capable of handling various types of web pages by making HTTP requests and parsing the received HTML content. This tool can be particularly useful for web scraping tasks, data collection, or extracting specific information from websites",
    mcpConfigName: "firecrawlMcpSettings"
  },
  {
    group: "Data & Analytics",
    name: "SerperDevTool",
    icon: "web-scraper-tool",
    displayName: "Serper Dev Tool",
    description:
      "This tool is designed to perform a semantic search for a specified query from a text’s content across the internet. It utilizes the serper.dev API to fetch and display the most relevant search results based on the query provided."
  },
  {
    group: "Data & Analytics",
    name: "SearchFlightTool",
    displayName: "Search Flight",
    icon: "excel-read-tool",
    description:
      "This tool is a flight discovery and comparison system that helps users explore available flight options and analyze pricing across airlines and booking providers. The system aggregates flight data to present options based on origin, destination, travel dates, number of passengers, cabin class, and stop preferences. It enables users to compare prices, durations, and airlines, identify the cheapest or fastest flights, monitor price changes over time, and receive insights on whether prices are likely to rise or fall. Google Flight Search does not sell tickets directly; instead, it redirects users to airlines or travel agencies to complete bookings."
  },
  {
    group: "Data & Analytics",
    name: "DatabaseAutoSyncTool",
    displayName: "Database Auto Sync Tool",
    icon: "excel-read-tool",
    description:
      "This tool is a specialized tool for recursively ingesting complex, nested JSON data into a Microsoft SQL Server database. The tool automatically manages the database schema (Schema Evolution) by creating missing tables or adding missing columns (ALTER TABLE) based on the provided JSON structure. It ensures relational integrity by automatically generating UUID-based Primary Keys and Foreign Keys for nested lists. ADO.NET connection string is supported by system (do not ask to user) and a JSON object where the root key determines the primary table name."
  },
  {
    group: "Data & Analytics",
    name: "LinkedinProfileSearchMCP",
    displayName: "Linkedin Profile Search",
    icon: "linkedin",
    description:
      "Search for LinkedIn profiles with filters and extract detailed profile information, including work experience, education history, location and more. No cookies or account required.",
    mcpConfigName: "apifySettings"
  },
  {
    group: "Data & Analytics",
    name: "LinkedinCompanyEmployeesMCP",
    displayName: "Linkedin Company Employees",
    icon: "linkedin",
    description:
      "Extract all LinkedIn Company employees with filters and detailed profile information, including complete work experience, and more. No cookies or account required.",
    mcpConfigName: "apifySettings"
  },
  {
    group: "Data & Analytics",
    name: "InstagramScraperMCP",
    displayName: "Instagram Scraper",
    icon: "instagram",
    description:
      "Scrape and download Instagram posts, profiles, places, hashtags, photos, and comments. Get data from Instagram using one or more Instagram URLs or search queries. Export scraped data, run the scraper via API, schedule and monitor runs or integrate with other tools.",
    mcpConfigName: "apifySettings"
  },
  {
    group: "Search & Maps",
    name: "BraveSearchMCP",
    displayName: "Web Search (Brave)",
    icon: "brave",
    description: "This MCP server can be used for internet search",
    mcpConfigName: "braveSearchSettings"
  },
  {
    group: "Search & Maps",
    name: "GoogleMapsMCP",
    displayName: "Google Maps",
    icon: "google-maps",
    description: "This MCP server can be used to connect Google map service.",
    mcpConfigName: "googleMapMcpSettings"
  }
];

// KOA-16736: Unified tool option type for both static AGENT_TOOLS and dynamic KAction tools
export interface IAgentToolOption {
  group: string;
  name: string;
  displayName?: string;
  description: string;
  icon: string;
  mcpConfigName?: string;
  toolType: EAgentToolType;
  actionId?: string;
  // KAction-specific metadata for sidebar display
  actionCategory?: EActionCategory;
  actionType?: EDatasourceType;
}

/** Shape of one entry in a common-settings list as the agent tool UI needs it. */
export interface IMcpConfigOption {
  id: string;
  name: string;
}

/**
 * Settings entries offered for an MCP tool's config dropdown. Map actions resolve their provider
 * from the selected settings id at runtime (an Amap web-service id routes to Amap, a Google Map
 * MCP id to Google), so the "googleMapMcpSettings" group also lists the project's Amap web-service
 * settings — the same merge `designer-action-parameter-editor-actions.ts` applies to map actions.
 */
export function getMcpConfigOptions(
  commonSettings: ICommonSettings | undefined,
  mcpConfigName: string
): IMcpConfigOption[] {
  const settings = commonSettings as unknown as Record<string, IMcpConfigOption[] | null | undefined> | undefined;
  const own: IMcpConfigOption[] = settings?.[mcpConfigName] ?? [];
  // Same rule as the map actions: explicit web-service settings + client settings carrying a REST key.
  const amap: IMcpConfigOption[] = mcpConfigName === "googleMapMcpSettings" ? getAmapMapActionSettings(settings) : [];
  return [...own, ...amap];
}

export function actionToToolOption(action: IActionDefinitionDto): IAgentToolOption {
  return {
    group: action.kDatasource_Name || "Project Actions",
    name: action.name,
    displayName: action.displayName || action.name,
    description: action.description || `Executes ${action.name}`,
    icon: "ai-action",
    toolType: EAgentToolType.KAction,
    actionId: action.id,
    actionCategory: action.actionCategory,
    actionType: action.actionType
  };
}

// KOA-16736: Map an action's category/type to a friendly badge label and color
export function getActionCategoryBadge(
  actionCategory?: EActionCategory,
  actionType?: EDatasourceType
): { label: string; color: "purple" | "green" | "orange" | "blue" | "grey" } {
  if (actionCategory === EActionCategory.Custom) {
    return { label: "Custom Action", color: "orange" };
  }
  if (actionType === EDatasourceType.AIActions) {
    return { label: "AI Action", color: "purple" };
  }
  if (
    actionType === EDatasourceType.RestApi ||
    actionType === EDatasourceType.RestApiWOSwagger ||
    actionType === EDatasourceType.OData
  ) {
    return { label: "API Action", color: "blue" };
  }
  if (actionType === EDatasourceType.SAP) {
    return { label: "SAP Action", color: "blue" };
  }
  if (actionType === EDatasourceType.CSharpFunctions) {
    return { label: "Custom Action", color: "orange" };
  }
  if (actionType === EDatasourceType.SystemCSFunctions) {
    return { label: "System Function", color: "grey" };
  }
  if (
    actionType === EDatasourceType.MsSql ||
    actionType === EDatasourceType.Postgre ||
    actionType === EDatasourceType.ManagedDbPostgre ||
    actionType === EDatasourceType.Oracle ||
    actionType === EDatasourceType.ManagedDbOracle ||
    actionType === EDatasourceType.CustomDb ||
    actionType === EDatasourceType.ManagedDb
  ) {
    return { label: "Database Action", color: "green" };
  }
  return { label: "System Action", color: "grey" };
}

// KOA-16736: Format camelCase / PascalCase / snake_case action names into "Spaced Words"
// for display. e.g. "SendEmail" -> "Send Email", "send_email" -> "Send Email"
export function formatActionDisplayName(name: string): string {
  if (!name) return "";
  return (
    name
      // Split snake_case
      .replace(/_/g, " ")
      // Insert space between lowercase->uppercase boundary (camelCase)
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      // Insert space between consecutive caps then lowercase (e.g. "VKNNumber" -> "VKN Number")
      .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
      // Collapse multiple spaces
      .replace(/\s+/g, " ")
      .trim()
      // Capitalise first letter
      .replace(/^./, (c) => c.toUpperCase())
  );
}

export type ElkNode = Node<IAiAgentElkNodeData, "elk">;

export type CrewProcessType = "sequential" | "hierarchical";

export interface IAiAgentManagerNodeData {
  managerAgent?: ICrewManagerAgent;
  sourceHandleId: string;
  onAddAgent?: () => void;
  sourceHandles?: Array<{
    id: string;
    position: "left" | "right" | "top" | "bottom";
    leftPercent: number;
    topPercent: number;
  }>;
}

export type AiAgentFlowNode = ElkNode | Node<IAiAgentManagerNodeData, "manager">;

export const AI_AGENT_MANAGER_NODE_ID = "__ai_agent_manager__";
export const AI_AGENT_MANAGER_SOURCE_HANDLE_ID = "__ai_agent_manager_source__";
export const AI_AGENT_MANAGER_TARGET_HANDLE_PREFIX = "__ai_agent_manager_target__";

// elk layouting options can be found here:
// https://www.eclipse.org/elk/reference/algorithms/org-eclipse-elk-layered.html
const layoutOptions = {
  "elk.algorithm": "layered",
  "elk.direction": "RIGHT",
  "elk.layered.spacing.edgeNodeBetweenLayers": "40",
  "elk.spacing.nodeNode": "40",
  "elk.layered.nodePlacement.strategy": "SIMPLE",
  "elk.padding": "[top=15,left=15,bottom=450,right=359]"
};

const elk = new ELK();

// uses elkjs to give each node a layouted position
export const getLayoutedNodes = async (nodes: ElkNode[], edges: Edge[]) => {
  const graph = {
    id: "root",
    layoutOptions,
    children: nodes.map((n) => {
      const targetPorts = n.data.targetHandles.map((t) => ({
        id: t.id,

        // ⚠️ it's important to let elk know on which side the port is
        // in this example targets are on the left (WEST) and sources on the right (EAST)
        properties: {
          side: "WEST"
        }
      }));

      const sourcePorts = n.data.sourceHandles.map((s) => ({
        id: s.id,
        properties: {
          side: "EAST"
        }
      }));

      return {
        id: n.id,
        width: n.width ?? 196,
        height: n.height ?? 50,
        // ⚠️ we need to tell elk that the ports are fixed, in order to reduce edge crossings
        properties: {
          "org.eclipse.elk.portConstraints": "FIXED_ORDER"
        },
        // we are also passing the id, so we can also handle edges without a sourceHandle or targetHandle option
        ports: [{ id: n.id }, ...targetPorts, ...sourcePorts]
      };
    }),
    edges: edges.map((e) => ({
      id: e.id,
      sources: [e.sourceHandle || e.source],
      targets: [e.targetHandle || e.target]
    }))
  };

  const layoutedGraph = await elk.layout(graph);

  const layoutedNodes = nodes.map((node) => {
    const layoutedNode = layoutedGraph.children?.find((lgNode) => lgNode.id === node.id);

    return {
      ...node,
      position: {
        x: layoutedNode?.x ?? 0,
        y: layoutedNode?.y ?? 0
      }
    };
  });

  return layoutedNodes;
};

export const generateAIAgentNodesAndEdges = async (agents: ICrewAgent[]) => {
  const agentNodes: ElkNode[] = [];
  const edges: Edge[] = [];
  for (let i = 0; i < agents.length; i++) {
    const agent = agents[i];
    const node: ElkNode = {
      id: Guid.create().toString(),
      position: { x: 0, y: 0 },
      type: "elk",
      data: {
        metadata: agent,
        sourceHandles: [],
        targetHandles: [],
        setNodes: () => {}
      }
    };
    if (i > 0) {
      const prevNode = agentNodes[i - 1];
      const nodeHandleId = `${node.id}-t#${node.data.targetHandles.length + 1}`;
      const prevNodeHandleId = `${prevNode.id}-s#${prevNode.data.sourceHandles.length + 1}`;

      node.data.targetHandles.push({ id: nodeHandleId });
      prevNode.data.sourceHandles.push({ id: prevNodeHandleId });
      const edge: Edge = {
        id: `${prevNode.id}-${node.id}`,
        source: prevNode.id,
        sourceHandle: prevNodeHandleId,
        target: node.id,
        targetHandle: nodeHandleId
      };
      edges.push(edge);
    }
    agentNodes.push(node);
  }

  const nodes = await getLayoutedNodes(agentNodes, edges);

  return { nodes, edges };
};

const normalizeManagerAgent = (managerAgent?: ICrewManagerAgent | string | null): ICrewManagerAgent | undefined => {
  if (!managerAgent) {
    return undefined;
  }

  if (typeof managerAgent === "string") {
    const model = managerAgent.trim();
    return model ? { model } : undefined;
  }

  return managerAgent;
};

export const buildAIAgentFlowWithProcessType = (
  crewNodes: ElkNode[],
  crewEdges: Edge[],
  processType: CrewProcessType,
  managerAgent?: ICrewManagerAgent | string | null
): { nodes: AiAgentFlowNode[]; edges: Edge[] } => {
  const nodes: ElkNode[] = crewNodes.map((node) => ({
    ...node,
    data: {
      ...node.data,
      sourceHandles: [...(node.data.sourceHandles || [])],
      targetHandles: [...(node.data.targetHandles || [])]
    }
  }));
  const edges: Edge[] = processType === "hierarchical" ? [] : crewEdges.map((edge) => ({ ...edge }));

  if (processType !== "hierarchical") {
    return { nodes, edges };
  }

  const normalizedManagerAgent = normalizeManagerAgent(managerAgent);

  // Filter out the manager agent's elk node from visible flow — it's represented by the central manager node
  const managerName = normalizedManagerAgent?.name;
  const subAgentNodes = managerName ? nodes.filter((node) => node.data.metadata?.name !== managerName) : nodes;

  const centerX = subAgentNodes.length
    ? subAgentNodes.reduce((sum, node) => sum + node.position.x, 0) / subAgentNodes.length
    : 280;
  const centerY = subAgentNodes.length
    ? subAgentNodes.reduce((sum, node) => sum + node.position.y, 0) / subAgentNodes.length
    : 180;
  const radius = Math.max(220, Math.min(420, 180 + subAgentNodes.length * 24));

  subAgentNodes.forEach((node, index) => {
    const angle = (2 * Math.PI * index) / subAgentNodes.length - Math.PI / 2;
    node.position = {
      x: centerX + radius * Math.cos(angle),
      y: centerY + radius * Math.sin(angle)
    };
  });

  const managerSourceHandles: Array<{
    id: string;
    position: "left" | "right" | "top" | "bottom";
    leftPercent: number;
    topPercent: number;
  }> = [];

  const managerNode: Node<IAiAgentManagerNodeData, "manager"> = {
    id: AI_AGENT_MANAGER_NODE_ID,
    type: "manager",
    position: {
      x: centerX,
      y: centerY
    },
    data: {
      managerAgent: normalizedManagerAgent,
      sourceHandleId: AI_AGENT_MANAGER_SOURCE_HANDLE_ID,
      sourceHandles: managerSourceHandles
    },
    draggable: false,
    selectable: false,
    connectable: false,
    deletable: false,
    focusable: false
  };

  const managerEdges: Edge[] = subAgentNodes.map((node) => {
    const dx = node.position.x - centerX;
    const dy = node.position.y - centerY;
    const dxToManager = centerX - node.position.x;
    const dyToManager = centerY - node.position.y;

    const managerSourcePosition: "left" | "right" | "top" | "bottom" =
      Math.abs(dx) >= Math.abs(dy) ? (dx >= 0 ? "right" : "left") : dy >= 0 ? "bottom" : "top";

    const nodeTargetPosition: "left" | "right" | "top" | "bottom" =
      Math.abs(dxToManager) >= Math.abs(dyToManager)
        ? dxToManager >= 0
          ? "right"
          : "left"
        : dyToManager >= 0
          ? "bottom"
          : "top";

    const managerSourceHandleId = `${AI_AGENT_MANAGER_SOURCE_HANDLE_ID}-${node.id}-${managerSourcePosition}`;
    const targetDominant = Math.max(Math.abs(dxToManager), Math.abs(dyToManager)) || 1;
    const targetUnitX = dxToManager / targetDominant;
    const targetUnitY = dyToManager / targetDominant;

    // Place the sub-agent connector on the exact perimeter point facing manager.
    const targetLeftPercent = 50 + targetUnitX * 48;
    const targetTopPercent = 50 + targetUnitY * 48;

    const managerTargetHandleId = `${AI_AGENT_MANAGER_TARGET_HANDLE_PREFIX}-${node.id}-${nodeTargetPosition}::${targetLeftPercent.toFixed(
      2
    )}::${targetTopPercent.toFixed(2)}`;
    const distance = Math.hypot(dx, dy) || 1;
    const unitX = dx / distance;
    const unitY = dy / distance;

    // Place the manager connector exactly on the circular perimeter direction.
    const leftPercent = 50 + unitX * 48;
    const topPercent = 50 + unitY * 48;

    // Hierarchical mode should expose a single visible connector dot per sub-agent
    // for the manager edge and remove sequential/source handle dots.
    node.data.sourceHandles = [];
    node.data.targetHandles = [{ id: managerTargetHandleId }];
    managerSourceHandles.push({
      id: managerSourceHandleId,
      position: managerSourcePosition,
      leftPercent,
      topPercent
    });

    return {
      id: `${AI_AGENT_MANAGER_NODE_ID}-${node.id}`,
      source: AI_AGENT_MANAGER_NODE_ID,
      sourceHandle: managerSourceHandleId,
      target: node.id,
      targetHandle: managerTargetHandleId,
      className: "react-flow__edge--manager"
    };
  });

  return {
    nodes: [managerNode, ...subAgentNodes],
    edges: [...edges, ...managerEdges]
  };
};

export const generateAIAgentNodesAndEdgesWithProcessType = async (
  agents: ICrewAgent[],
  processType: CrewProcessType,
  managerAgent?: ICrewManagerAgent | string | null
): Promise<{ nodes: AiAgentFlowNode[]; edges: Edge[] }> => {
  const { nodes, edges } = await generateAIAgentNodesAndEdges(agents);
  return buildAIAgentFlowWithProcessType(nodes, edges, processType, managerAgent);
};

// export const EXAMPLE_DATA: ICrewAgentDTO = {
//   crewAgents: [
//     {
//       name: "FinancialDataResearcher",
//       role: "Internet Research Specialist",
//       goal: "Gather relevant financial data and information about the specified company for the year 2024.",
//       backstory:
//         "With expertise in sourcing financial information from the internet, I aim to provide comprehensive data collection to support financial analysis efforts.",
//       task: {
//         description:
//           "Conduct thorough research to gather relevant financial data and information about {company_name} for the year 2024. Collate information on trends, forecasts, and key financial metrics available online.",
//         expected_output: "A collection of financial information and data regarding {company_name} for the year 2024.",
//         output_format: {
//           financial_data:
//             "List[FinancialData] (A list of objects containing URLs and texts with relevant financial data and trends for {company_name} in 2024)"
//         }
//       },
//       tools: ["SerperDevTool", "ScrapeWebsiteTool"]
//     },
//     {
//       name: "FinancialAnalyst",
//       role: "Financial Analysis Expert",
//       goal: "Analyze the gathered financial data and prepare a comprehensive financial status report for 2024.",
//       backstory:
//         "With a background in financial analysis, I specialize in interpreting data to provide insights into a company's financial health and forecast future performance.",
//       task: {
//         description:
//           "Examine the financial data gathered by FinancialDataResearcher, focusing on interpreting key indicators and trends. Prepare an analysis highlighting the financial status and outlook for {company_name} in 2024.",
//         expected_output:
//           "A detailed analysis of {company_name}'s financial status for 2024, offering insights into their financial health and strategic recommendations.",
//         output_format: {
//           financial_report:
//             "FinancialReport (A comprehensive report detailing financial status, key metrics, and strategic insights for {company_name} in 2024)"
//         }
//       },
//       tools: []
//     }
//   ],
//   crew_inputs: [
//     {
//       parameter_name: "company_name",
//       parameter_format: "str"
//     }
//   ],
//   crew_custom_data_types: [
//     {
//       type_name: "FinancialData",
//       properties: {
//         url: "str (The URL of the webpage containing relevant financial information)",
//         content: "str (The text or content extracted from the webpage)"
//       }
//     },
//     {
//       type_name: "FinancialReport",
//       properties: {
//         financial_status: "str (A summary of the financial status of the company)",
//         key_metrics: "List[str] (Key financial metrics and indicators)",
//         strategic_insights: "str (Strategic insights and recommendations based on the financial analysis)"
//       }
//     }
//   ]
// };
